<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';
import { isCategoryActivated } from '@/refs/categories.refs';

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Refs & données
// -------------------------------
import { idToDelete } from '@/refs/commerce.refs';
import { isGlobalError } from '@/refs/common.refs';

// -------------------------------
// 🔹 Mutations / services
// -------------------------------
import { UserMutation } from '@/mutations/user.mutation';
import { TaxeMutation } from '@/mutations/taxe.mutation';
import { form_Client_user_Body } from '@/refs/user.refs';
import {
  form_taxe_Body,
  isAtcivatedWatch,
  isAtcivatedWatchTaxe,
  isUpdateFromTaxeActivated,
} from '@/refs/taxe.refs';
const { handleUpdateUserCustomerById, handleDeleteUserClientCustomer } =
  UserMutation();
const { handleCreateTaxe, handleUpdateTaxById } = TaxeMutation();
// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

// -------------------------------
// 🔹 Gestion du formulaire
// -------------------------------

/**
 * Soumet le formulaire de création de commerce.
 * Gère les différentes étapes : création du commerce, ajout du contact, ou upload du logo.
 */

const handleCreate = async () => {
  isLoaderLoginFormActivated.value = true;

  try {
    const res = await handleCreateTaxe({
      name: form_taxe_Body.name,
      rate: Number(form_taxe_Body.rate),
      type: form_taxe_Body?.type,
      description: form_taxe_Body.description,
      isActive: Boolean(form_taxe_Body.isActive),
    });

    if (res) {
      setTimeout(() => {
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        isLoaderLoginFormActivated.value = false;
        handleClose();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isLoaderLoginFormActivated.value = false;
      isGlobalError.value = false;
    }, 1000);
  }
};

const handleUpdate = async () => {
  isLoaderLoginFormActivated.value = true;

  try {
    const res = await handleUpdateTaxById({
      name: form_taxe_Body.name,
      rate: Number(form_taxe_Body.rate),
      description: form_taxe_Body.description,
      isActive: Boolean(form_taxe_Body.isActive),
    });

    if (res) {
      setTimeout(() => {
        isAtcivatedWatch.value = !isAtcivatedWatch.value;
        isAtcivatedWatchTaxe.value = !isAtcivatedWatchTaxe.value;
        isLoaderLoginFormActivated.value = false;
        handleClose();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isLoaderLoginFormActivated.value = false;
      isGlobalError.value = false;
    }, 1000);
  }
};
const handleSubmit = async () => {
  if (isUpdateFromTaxeActivated.value) {
    handleUpdate();
  } else {
    handleCreate();
  }
};

/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  isUpdateFromTaxeActivated.value = false;
  idToDelete.value = null;
  emit('HandleClose');
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div class="space-y-[16px]">
      <TextInput
        required="true"
        v-model="form_taxe_Body.name"
        name="Entrez le nom de la taxe"
        placeholder="Entrez le nom de la taxe"
        type="text"
        id="Entrez le nom de la taxe"
        label="Entrez le nom de la taxe"
      />

      <TextInput
        required="true"
        v-model="form_taxe_Body.rate"
        name="Entrez le taux de la taxe"
        placeholder="Entrez le taux de la taxe"
        type="number"
        id="Entrez le taux de la taxe"
        label="Entrez le taux de la taxe"
      />

      <TextInput
        required="true"
        v-model="form_taxe_Body.description"
        name="Entrez la description de la taxe"
        placeholder="Entrez la description de la taxe"
        type="text"
        id="Entrez la description de la taxe"
        label="Entrez la description de la taxe"
      />

      <div class="flex flex-col gap-2" v-show="!isUpdateFromTaxeActivated">
        <label
          for="taxe"
          class="text-14px font-tt-medium-Normal leading-normal"
        >
          Veuillez sélectionner le type de taxe
          <span class="text-decroissance text-14px leading-normal">*</span>
        </label>

        <select
          required
          id="taxe"
          name="taxe"
          v-model="form_taxe_Body.type"
          class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
        >
          <option value="" disabled selected>Choisissez un type de taxe</option>
          <option value="vat">Taxe sur la valeur ajoutée (TVA)</option>
          <option value="gst">Taxe sur les biens et services (GST)</option>
          <option value="sales">Taxe sur les ventes</option>
          <option value="excise">Taxe d'accise</option>
          <option value="customs">Droits de douane</option>
          <option value="income">Impôt sur le revenu</option>
          <option value="property">Taxe foncière / immobilière</option>
          <option value="local">Taxe locale</option>
          <option value="other">Autre type de taxe</option>
        </select>
      </div>

      <div class="flex flex-col gap-2">
        <label
          for="taxe"
          class="text-14px font-tt-medium-Normal leading-normal"
        >
          Veuillez sélectionner son statut
          <span class="text-decroissance text-14px leading-normal">*</span>
        </label>

        <select
          required
          id="taxe"
          name="taxe"
          v-model="form_taxe_Body.isActive"
          class="border-border-input focus:border-primary-900 focus:ring-primary-900 box-border h-(--input-height) rounded-[10px] border px-3 py-1 outline-0 focus:ring-1"
        >
          <option value="true">Actif</option>
          <option value="false">Inatif</option>
        </select>
      </div>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            type="submit"
            :isClicked="isLoaderLoginFormActivated"
            :label="isCategoryActivated ? 'Modifier' : 'Enregistrer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            label="Annuler"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
